import React, { ReactNode } from 'react';

interface PanelProps {
  /** Panel title shown in the center of the title bar */
  title: string;
  /** Called when the red (close) button is clicked */
  onClose?: () => void;
  /** Panel content */
  children: ReactNode;
  /** Optional className for the content area */
  contentClassName?: string;
  /** Optional header right element */
  headerRight?: ReactNode;
  'data-testid'?: string;
}

/**
 * Panel — macOS-style app window container.
 * Renders a title bar with traffic-light controls (red/yellow/green) and a content area.
 * No dragging or resizing — purely visual macOS aesthetic.
 */
export default function Panel({
  title,
  onClose,
  children,
  contentClassName = '',
  headerRight,
  'data-testid': testId,
}: PanelProps) {
  return (
    <div
      className="flex flex-col w-full h-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden panel-enter"
      data-testid={testId || 'panel-container'}
    >
      {/* Title bar */}
      <div
        className="flex items-center px-3 md:px-4 shrink-0 select-none border-b border-gray-200"
        style={{ height: 40, backgroundColor: '#f9fafb' }}
      >
        {/* Traffic lights — always visible for macOS feel */}
        <div className="flex items-center gap-1.5 w-14 md:w-16 shrink-0" data-testid="panel-traffic-lights">
          <button
            onClick={onClose}
            className="w-3 h-3 rounded-full transition-opacity hover:opacity-80 active:opacity-60"
            style={{ backgroundColor: '#FF5F56' }}
            title="Close"
            data-testid="panel-close-button"
          />
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FFBD2E' }} />
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#27C93F' }} />
        </div>

        {/* Title */}
        <div className="flex-1 text-center">
          <span className="text-sm font-semibold text-gray-700 truncate block px-2">{title}</span>
        </div>

        {/* Right slot */}
        <div className="flex w-14 md:w-16 justify-end">
          {headerRight}
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 overflow-auto bg-white ${contentClassName}`}>
        {children}
      </div>
    </div>
  );
}
